import dayjs from "dayjs";
import {
  QueryCondition,
  QueryOperation,
  SearchQuery,
} from "../code/search-query/search-query";
import { DataColumn } from "../controls/Data.default";
import _ from "lodash";
import { hasValue } from "../utils/string";
import { InputObject } from "../controls/default";
import { EmployeeUser } from "../repository/data.default";
import { nameOf } from "../utils/typescript";

export interface EmployeesViewForm {
  readonly userName: string;
  readonly firstName: string;
  readonly middleName: string;
  readonly lastName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly company?: InputObject;
}

export const employeesViewColumns: DataColumn[] = [
  {
    title: "Company Name",
    key: "companyName",
    width: 192,
    sort: true,
        fixed: 'left'
  },
  {
    title: "User Name",
    key: "userName",
    width: 192,
    sort: true,
  },
  {
    title: "First Name",
    key: "firstName",
    width: 192,
    sort: true,
  },
  {
    title: "Middle Name",
    key: "middleName",
    width: 192,
    sort: true,
  },
  {
    title: "Last Name",
    key: "lastName",
    width: 192,
    sort: true,
  },
  {
    title: "Date Of Birth",
    key: "dateOfBirth",
    width: 128,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Email",
    key: "email",
    width: 256,
    sort: true,
  },
  {
    title: "Email Confirmed",
    key: "emailConfirmed",
    width: 128,
    sort: true,
  },
  {
    title: "Phone",
    key: "phoneNumber",
    width: 192,
    sort: true,
  },
  {
    title: "Company Phone",
    key: "companyPhoneNumber",
    width: 192,
    sort: true,
  },
  // PD-02 §6.6.1 — required employee grid columns.
  {
    title: "Registration Date",
    key: "joinedDate",
    width: 160,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Last Login",
    key: "lastLoginAt",
    width: 160,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
  {
    title: "Status",
    key: "lockoutEnabled",
    width: 128,
    render: (value: boolean, record: EmployeeUser) => {
      const now = Date.now();
      const lockedUntil = record.lockoutEnd ? new Date(record.lockoutEnd).getTime() : null;
      const isLocked = !!value && lockedUntil != null && lockedUntil > now;
      return isLocked ? "Inactive" : "Active";
    },
    sort: true,
  },
  {
    title: "Deactivation Date",
    key: "lockoutEnd",
    width: 160,
    format: "DD/MM/YYYY",
    sort: true,
  },
];

export function mapEmployeesViewQuery(values: EmployeesViewForm): SearchQuery {
  const conditions: QueryCondition[] = Object.entries(values)
        .filter(([key, value]) =>
      hasValue(value),
    )
    .map(([key, value]) => {
      switch (key) {
        case nameOf<EmployeesViewForm>("company"):
          return {
            field: nameOf<EmployeeUser>("companyUid"),
            values: [(value as InputObject).value],
            operation: QueryOperation.Equal,
          };
        case nameOf<EmployeesViewForm>("userName"):
          return {
            field: key,
            values: [value],
            operation: QueryOperation.StartsWith,
          };
        case nameOf<EmployeesViewForm>("firstName"):
          return {
            field: key,
            values: [value],
            operation: QueryOperation.StartsWith,
          };
        case nameOf<EmployeesViewForm>("middleName"):
          return {
            field: key,
            values: [value],
            operation: QueryOperation.StartsWith,
          };
        case nameOf<EmployeesViewForm>("lastName"):
          return {
            field: key,
            values: [value],
            operation: QueryOperation.StartsWith,
          };
        case nameOf<EmployeesViewForm>("dateOfBirth"):
          return {
            field: key,
            values: [dayjs(value).format("YYYY-MM-DD")],
            operation: QueryOperation.Equal,
          };
        case nameOf<EmployeesViewForm>("email"):
          return {
            field: key,
            values: [value],
            operation: QueryOperation.StartsWith,
          };
        case nameOf<EmployeesViewForm>("phoneNumber"):
          return {
            field: key,
            values: [value],
            operation: QueryOperation.StartsWith,
          };
      }
    })
    .filter((f) => !!f) as QueryCondition[];

  return {
    conditions,
  };
}
